import React, { useEffect, useMemo, useState } from 'react';
import { FiExternalLink, FiList, FiSearch, FiX } from 'react-icons/fi';
import { getToken } from '../auth';

const PdfOutlineModal = ({ filename, API_URL, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [sortMode, setSortMode] = useState('document');

  useEffect(() => {
    if (!filename) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setEntries([]);
    setPageCount(0);
    setTruncated(false);
    setFilterText('');
    setSortMode('document');

    const headers = { Accept: 'application/json' };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    fetch(`${API_URL}/files/${encodeURIComponent(filename)}/outline`, {
      headers,
      signal: controller.signal,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.message || 'Failed to read PDF outline');
        return body;
      })
      .then((body) => {
        setEntries(Array.isArray(body.entries) ? body.entries : []);
        setPageCount(Number(body.pageCount || 0));
        setTruncated(Boolean(body.truncated));
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setError(err?.message || 'Failed to read PDF outline');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [filename, API_URL]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const visibleEntries = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    const filtered = needle
      ? entries.filter((entry) => String(entry.title || '').toLowerCase().includes(needle))
      : [...entries];

    const byTitle = (a, b) => String(a.title || '').localeCompare(
      String(b.title || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    );
    const byPage = (a, b) => {
      const aPage = Number.isFinite(Number(a.page)) ? Number(a.page) : Number.MAX_SAFE_INTEGER;
      const bPage = Number.isFinite(Number(b.page)) ? Number(b.page) : Number.MAX_SAFE_INTEGER;
      return aPage - bPage;
    };

    if (sortMode === 'title-asc') filtered.sort((a, b) => byTitle(a, b) || a.index - b.index);
    if (sortMode === 'title-desc') filtered.sort((a, b) => byTitle(b, a) || a.index - b.index);
    if (sortMode === 'page-asc') filtered.sort((a, b) => byPage(a, b) || byTitle(a, b));
    if (sortMode === 'page-desc') filtered.sort((a, b) => byPage(b, a) || byTitle(a, b));
    return filtered;
  }, [entries, filterText, sortMode]);

  const openPage = (entry) => {
    const page = Number(entry?.page);
    if (!Number.isInteger(page) || page < 1) return;
    const token = getToken();
    const url = `${API_URL}/files/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}#page=${page}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      role="presentation"
      className="upload-spinner-overlay"
      style={{ backgroundColor: 'rgba(0,0,0,0.58)', zIndex: 10050 }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-outline-title"
        className="upload-spinner-box"
        style={{
          width: 900,
          maxWidth: '95vw',
          height: '82vh',
          maxHeight: 850,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text)',
        }}
      >
        <div style={header}>
          <div style={{ minWidth: 0 }}>
            <h3 id="pdf-outline-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiList />
              <span>Document Outline</span>
            </h3>
            <div title={filename} style={filenameStyle}>{filename}</div>
          </div>
          <button type="button" className="btn" onClick={onClose} aria-label="Close document outline">
            <FiX />
          </button>
        </div>

        <div style={controls}>
          <label style={searchWrapper}>
            <FiSearch aria-hidden style={searchIcon} />
            <input
              type="search"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              placeholder="Filter section/bookmark names"
              aria-label="Filter section or bookmark names"
              style={searchInput}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span>Sort</span>
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              style={select}
            >
              <option value="document">Document order</option>
              <option value="title-asc">Section/Bookmark A–Z</option>
              <option value="title-desc">Section/Bookmark Z–A</option>
              <option value="page-asc">Page ascending</option>
              <option value="page-desc">Page descending</option>
            </select>
          </label>
        </div>

        <div style={summary}>
          <div>
            {loading
              ? 'Reading PDF outline…'
              : `${visibleEntries.length.toLocaleString()} of ${entries.length.toLocaleString()} outline entries`
                + (pageCount ? ` · ${pageCount.toLocaleString()} PDF pages` : '')}
          </div>
          {!loading && !error && entries.length > 0 && (
            <div style={helperText}>
              Click on a section/bookmark to go directly to the page.
            </div>
          )}
        </div>

        <div style={content}>
          {loading && <div style={emptyState}>Reading outline metadata…</div>}
          {!loading && error && <div style={{ ...emptyState, color: '#b71c1c' }}>{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div style={emptyState}>This PDF does not contain a document outline.</div>
          )}
          {!loading && !error && entries.length > 0 && visibleEntries.length === 0 && (
            <div style={emptyState}>No outline titles match this filter.</div>
          )}
          {!loading && !error && visibleEntries.length > 0 && (
            <table style={table}>
              <colgroup>
                <col style={{ width: '58%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <thead style={thead}>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Section/Bookmark name</th>
                  <th style={{ ...th, textAlign: 'center' }}>Page</th>
                  <th style={{ ...th, textAlign: 'center' }}>Outline #</th>
                  <th style={{ ...th, textAlign: 'center' }}>Open</th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((entry) => {
                  const hasPage = Number.isInteger(Number(entry.page)) && Number(entry.page) > 0;
                  return (
                    <tr
                      key={`${entry.index}-${entry.title}`}
                      style={{ cursor: hasPage ? 'pointer' : 'default' }}
                      onClick={() => openPage(entry)}
                      title={hasPage ? `Open page ${entry.page}` : 'No page destination'}
                    >
                      <td style={{
                        ...td,
                        textAlign: 'left',
                        paddingLeft: 12 + Math.min(Number(entry.depth || 0), 12) * 18,
                      }}>
                        {entry.title || '(Untitled)'}
                      </td>
                      <td style={{ ...td, ...numericCell }}>{hasPage ? entry.page : '—'}</td>
                      <td style={{ ...td, ...numericCell, opacity: 0.7 }}>{Number(entry.index || 0) + 1}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {hasPage && <FiExternalLink aria-label="Open page" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {truncated && (
          <div style={warning}>The outline was truncated after 10,000 entries.</div>
        )}
      </div>
    </div>
  );
};

const header = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '16px 18px 12px',
  borderBottom: '1px solid var(--border)',
};

const filenameStyle = {
  marginTop: 4,
  maxWidth: '72vw',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--text-muted, #666)',
  fontSize: 12,
};

const controls = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
  padding: '12px 18px',
  borderBottom: '1px solid var(--border)',
};

const searchWrapper = {
  position: 'relative',
  display: 'flex',
  flex: '1 1 260px',
  minWidth: 220,
};

const searchIcon = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  opacity: 0.65,
};

const searchInput = {
  width: '100%',
  padding: '8px 10px 8px 34px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)',
};

const select = {
  padding: '8px 9px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  backgroundColor: 'var(--card-bg)',
  color: 'var(--text)',
};

const summary = {
  padding: '8px 18px',
  borderBottom: '1px solid var(--border)',
  fontSize: 12,
  opacity: 0.8,
};

const helperText = {
  marginTop: 4,
  fontWeight: 600,
  opacity: 0.9,
};

const content = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
};

const emptyState = {
  padding: 36,
  textAlign: 'center',
  fontStyle: 'italic',
};

const table = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thead = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: 'var(--table-head-bg)',
};

const th = {
  padding: '10px 12px',
  textAlign: 'left',
  color: 'var(--text)',
  borderBottom: '1px solid var(--border)',
};

const td = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
  overflowWrap: 'anywhere',
};

const numericCell = {
  textAlign: 'center',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
};

const warning = {
  padding: '9px 18px',
  color: '#8a5a00',
  backgroundColor: 'rgba(255,193,7,0.12)',
  fontSize: 12,
};

export default PdfOutlineModal;
